import {
	alpha,
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	IconButton,
	ListItem,
	Modal,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { ASSETS_BASE_URL, SERVER_BASE_URL } from "../../config";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import customHttp from "../../services/customHTTP";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { useNavigate } from "react-router-dom";

const MultipleModelViewer = ({
	show,
	setShow,
	machine_number,
	machine_name,
	machine_id,
	machine_product_name = "",
}) => {
	const [models, setModels] = useState([]);
	const [loading, setLoading] = useState(false);
	const { user } = useAuth();
	const { selectedSite, selectedSiteCurrency } = useSite();
	const navigate = useNavigate();
	const theme = useTheme();
	const default_img = `${ASSETS_BASE_URL}/stock.png`;
	const productImageUrl = `${ASSETS_BASE_URL}/MachineImages/${machine_number}.png`;
	const style = (theme) => ({
		position: "absolute",
		top: "50%",
		left: "50%",
		width: "80vw",
		transform: "translate(-50%, -50%)",

		bgcolor: alpha(theme.palette.background.paper, 1),
		boxShadow: 24,
		borderRadius: 2,
		p: 2,
		px: 6,
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
	});

	const handleCloseDialog = () => {
		setShow(false);
	};

	useEffect(() => {
		const checkResources = async () => {
			try {
				let index = 1;
				const foundModels = [];

				while (true) {
					const suffix = index.toString().padStart(2, "0");
					const filename = `${machine_number}_${suffix}`;
					const filepath = `${SERVER_BASE_URL}/stock-models/3D/${filename}.html`;

					const res = await fetch(filepath, { method: "HEAD" });

					if (res.ok) {
						foundModels.push({ filename, filepath });
						index++;
					} else {
						break;
					}
				}

				setModels(foundModels);
			} catch (error) {
				console.warn("Resource check failed. Using defaults.");
			}
		};

		checkResources();
	}, [machine_number]);

	const handleModelOpen = async (item) => {
		setLoading(true); // Show loading state
		console.log(theme);
		try {
			// Make the API call
			const response = await customHttp.get("/models/process-modelfile", {
				params: {
					filename: item.filename,
					machinename: machine_name,
					currency: selectedSiteCurrency.toUpperCase(),
					loggedinUser: user.user_id,
					ecommachineid: machine_id,
					baseColor: theme.palette.primary.main,
					selectedSite,
					machinenumber: machine_number,
				},
			});

			if (response.status === 200) {
				const backendUrl = SERVER_BASE_URL || "";
				const fullUrl = backendUrl + response.data.url; // Construct the full URL
				setLoading(false);
				window.open(fullUrl); // Redirect after 1 second delay
				// navigate("/cart");
			}
		} catch (err) {
			console.error("Error fetching the model:", err);
		}
	};

	return (
		<Modal
			open={show}
			onClose={(e) => {
				e.stopPropagation(); // Prevent the click event from propagating
				handleCloseDialog();
			}}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description">
			<Box sx={style}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
						mt: 1,
						width: "100%",
					}}>
					<Box>
						<Typography variant="h5" sx={{ m: 0, fontWeight: "bold" }}>
							Choose a Model{" "}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							<b>{machine_name}</b> machine has <b>multiple 3D models </b> available. Please select one to
							continue.
						</Typography>
					</Box>
					<IconButton
						aria-label="close"
						onClick={(e) => {
							e.stopPropagation(); // Prevent the click event from propagating
							handleCloseDialog();
						}}>
						<CloseIcon />
					</IconButton>
				</Box>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						alignItems: "center",
						mb: 2,
						mt: 1,
						columnGap: "15px",
						flexWrap: "wrap",
						maxHeight: "60vh",
						overflowY: "auto",
					}}>
					{models.map((item) => (
						<Tooltip title={`View 3D Model: ${item.filename}`}>
							<Card
								className="machine-card"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									handleModelOpen(item);
								}}>
								<CardActionArea className="machine-card-content">
									{" "}
									{/* Added onClick */}
									<CardMedia
										component="img"
										image={`${ASSETS_BASE_URL}/MachineImages/${item.filename}.png`} // Use the random image here
										alt={item.filename}
										className="machine-img"
										onError={(e) => {
											e.target.src = default_img;
										}}
									/>
									<CardContent sx={{ paddingBottom: "10px", textAlign: "center" }}>
										<Tooltip title={item.filename}>
											<Typography
												variant="body1"
												sx={{
													fontWeight: "bold",
													lineHeight: "1.2rem",
													marginBottom: "5px",
													display: "-webkit-box",
													overflow: "hidden",
													WebkitLineClamp: 2,
													WebkitBoxOrient: "vertical",
													textOverflow: "ellipsis",
													minHeight: "2.4rem",
													color: "#000", // Adjust based on the line height to ensure 2 lines of space
												}}>
												{item.filename}
											</Typography>
										</Tooltip>
										<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
											<b>Machine Serial #:</b>{" "}
											{machine_product_name && machine_product_name !== "EMPTY"
												? machine_product_name
												: "N/A"}
										</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
											<b>Machine #:</b> {machine_number}
										</Typography>

										<div className="action-buttons">
											<Tooltip title="View 3D Model">
												<IconButton
													aria-label="View 3D Model"
													size="large"
													onClick={(e) => {
														e.stopPropagation();
														e.preventDefault();
														handleModelOpen(item);
													}}
													disabled={loading}
													className={loading ? "blinking-icon" : ""}>
													<ViewInArOutlinedIcon />
												</IconButton>
											</Tooltip>
										</div>
									</CardContent>
								</CardActionArea>
							</Card>
						</Tooltip>
					))}
				</Box>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						alignItems: "center",
						mb: 2,
						mt: 1,
						width: "100%",
					}}>
					<Button
						variant="outlined"
						onClick={(e) => {
							e.stopPropagation(); // Prevent the click event from propagating
							handleCloseDialog();
						}}>
						Close
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default MultipleModelViewer;
